import { supabase } from '../../../supabaseClient';

// ── Configurables (no magic numbers in logic) ────────────────────────────────
export const EXCHANGE_CACHE_KEY = 'td:exchange-rate';
export const EXCHANGE_TTL_MS = 60 * 60 * 1000; // 1 hour

/** Valida que la tasa tenga USD/EUR finitos y positivos. */
function isValidRate(rates) {
  const usd = Number(rates?.USD);
  const eur = Number(rates?.EUR);
  return Number.isFinite(usd) && usd > 0 && Number.isFinite(eur) && eur > 0;
}

/**
 * Obtiene la tasa oficial BCV (USD/EUR) vía la Edge Function existente.
 * Cachea en localStorage con TTL de 1 hora.
 * @param {{ forceRefresh?: boolean }} [opts]
 * @returns {Promise<{USD:number, EUR:number, updatedAt:string|null, fetchedAt:number}|null>}
 */
export async function getOfficialRate({ forceRefresh = false } = {}) {
  const cached = readCache();
  if (!forceRefresh && cached && isFresh(cached)) {
    return cached;
  }

  // Fast fetch helper with timeout
  const fetchWithTimeout = (url, ms = 2000) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), ms);
    return fetch(url, { signal: controller.signal })
      .finally(() => clearTimeout(timer));
  };

  try {
    // Try primary Edge Function with fast timeout
    const invokePromise = supabase.functions.invoke('hub-currency');
    const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 1500));
    const { data, error } = await Promise.race([invokePromise, timeoutPromise]);
    if (error) throw error;
    if (!isValidRate(data?.rates)) throw new Error('invalid exchange rate');
    const rate = {
      USD: Number(data.rates.USD),
      EUR: Number(data.rates.EUR),
      updatedAt: data?.updatedAt ?? null,
      fetchedAt: Date.now(),
    };
    writeCache(rate);
    return rate;
  } catch (err) {
    try {
      const [usdRes, eurRes] = await Promise.all([
        fetchWithTimeout('https://ve.dolarapi.com/v1/dolares/oficial', 2000),
        fetchWithTimeout('https://ve.dolarapi.com/v1/euros/oficial', 2000)
      ]);
      if (usdRes.ok && eurRes.ok) {
        const usdData = await usdRes.json();
        const eurData = await eurRes.json();
        const rate = {
          USD: Number(usdData.promedio),
          EUR: Number(eurData.promedio),
          updatedAt: usdData.fechaActualizacion || null,
          fetchedAt: Date.now(),
        };
        if (isValidRate({ USD: rate.USD, EUR: rate.EUR })) {
          writeCache(rate);
          return rate;
        }
      }
    } catch (_) {
      // Ignorar error de fallback
    }

    if (cached) return cached; // fallback a caché vencida si existe
    return null;
  }
}

/** Convierte un monto VES a USD usando la tasa oficial. */
export function convertToUSD(vesAmount, rate) {
  if (!rate || !rate.USD) return null;
  return Number((vesAmount / rate.USD).toFixed(2));
}

/** Devuelve el precio en ambas monedas. */
export function priceInBoth(vesAmount, rate) {
  return {
    ves: vesAmount,
    usd: convertToUSD(vesAmount, rate),
  };
}

// ── Helpers internos de cache ─────────────────────────────────────────────────
function readCache() {
  try {
    const raw = localStorage.getItem(EXCHANGE_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

function writeCache(rate) {
  try {
    localStorage.setItem(EXCHANGE_CACHE_KEY, JSON.stringify(rate));
  } catch (_) {
    // almacenamiento no disponible: ignorar
  }
}

function isFresh(rate) {
  return Date.now() - (rate.fetchedAt || 0) < EXCHANGE_TTL_MS;
}
